import React, { useState, useCallback } from 'react';
import InputParameterTable from './InputParameterTable';
import FieldMappingDrawer from './FieldMappingDrawer';
import {TInputParameter, TUpsertISRProjectDataModelsInput} from "../../Pages/DefineModel/model";
import Typography from 'Components/DesignSystem/Typography/Typography';

// Simple unique ID generator
const generateId = (): string => {
    return `param_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

type InputParameterConfigProps = {
    id: string;
    projectId: string;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
}

const InputParameterConfig = ({
                                  id,
                                  projectId,
                                  userProjectDataModels,
                                  setUserProjectDataModels
                              }: InputParameterConfigProps) => {
    const [parameters, setParameters] = useState<TInputParameter[]>(userProjectDataModels.project.inputParameters ?? []);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingParameterId, setEditingParameterId] = useState<string | null>(null);

    // Load existing parameters from model data
    // useEffect(() => {
    //     if (userProjectDataModels?.project?.inputParameters) {
    //         setParameters(userProjectDataModels.project.inputParameters);
    //     }
    // }, [userProjectDataModels]);    // Load existing parameters from model data

    const saveInputParameter =(parameters: TInputParameter[]) => {
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            return {
                ...prevDataModels,
                project: {
                    ...prevDataModels.project,
                    inputParameters: parameters,
                },
            };
        });
    }

    const handleAddParameter = useCallback(() => {
        const newParameter: TInputParameter = {
            id: generateId(),
            paramKey: '',
            subModelId: '',
            fieldId: '',
            createdAt: new Date().toISOString(),
            required: true
        };
        setParameters(prev => {
            const updated = [...prev, newParameter];
            saveInputParameter(updated);
            return updated;
        });
    }, []);

    const handleDeleteParameter = useCallback((parameterId: string) => {
        setParameters(prev => {
            const parameters = prev.filter(p => p.id !== parameterId);
            saveInputParameter(parameters);
            return parameters;
        });
    }, []);

    const handleUpdateParameter = useCallback((parameterId: string, updates: Partial<TInputParameter>) => {
        setParameters(prev => {
            const parameters = prev.map(p =>
                p.id === parameterId ? {...p, ...updates} : p
            )
            saveInputParameter(parameters);
            return parameters;
        });
    }, []);

    const handleOpenFieldSelector = useCallback((parameterId: string) => {
        setEditingParameterId(parameterId);
        setIsDrawerOpen(true);
    }, []);

    const handleFieldSelected = useCallback((fieldId: string, subModelId: string | undefined, displayName: string) => {
        if (editingParameterId) {
            handleUpdateParameter(editingParameterId, {
                subModelId: subModelId ?? '',
                fieldId: fieldId
            });
        }
        setIsDrawerOpen(false);
        setEditingParameterId(null);
    }, [editingParameterId, handleUpdateParameter]);

    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setEditingParameterId(null);
    }, []);

    // Validate parameter name
    const validateParameterName = useCallback((name: string, currentId: string): string | null => {
        const trimmed = name.trim();
        if (!trimmed) {
            return 'Parameter name is required';
        }
        // Check for duplicates (case-insensitive, using trimmed value)
        const isDuplicate = parameters.some(
            p => p.id !== currentId && p.paramKey.toLowerCase() === trimmed.toLowerCase()
        );
        if (isDuplicate) {
            return 'Parameter already exists';
        }
        return null;
    }, [parameters]);

    return (
        <div id={id} className="tw-h-full tw-pt-4 tw-ps-4 tw-bg-grey-100">
            <div className="tw-p-6 tw-bg-white">
            {/* Header */}
            <div className="tw-mb-4">
                <Typography variant="heading-2" className="tw-text-grey-900">
                    Input Parameter
                </Typography>
            </div>

            {/* Warning Note */}
            <div id="input-parameter-warning" className="tw-max-w-4xl tw-mb-6">
                <div className="tw-bg-tone-warning-tint tw-px-2 tw-py-1 tw-rounded-sm tw-flex tw-items-center">
                    <Typography variant="sub-body-supporting" className="tw-text-yellow-warning-900">
                        Note: Ensure field names are unique across all submodels to avoid mapping conflicts
                    </Typography>
                </div>
            </div>

            {/* Parameters Table */}
            <InputParameterTable
                parameters={parameters}
                onAddParameter={handleAddParameter}
                onDeleteParameter={handleDeleteParameter}
                onUpdateParameter={handleUpdateParameter}
                onOpenFieldSelector={handleOpenFieldSelector}
                validateParameterName={validateParameterName}
                userProjectDataModels={userProjectDataModels}
            />

            {/* Field Selection Drawer */}
            <FieldMappingDrawer
                isOpen={isDrawerOpen}
                onClose={handleCloseDrawer}
                onFieldSelected={handleFieldSelected}
                userProjectDataModels={userProjectDataModels}
                selectedFields={parameters
                    .filter(p => p.fieldId && p.id !== editingParameterId)
                    .map(p => ({ subModelId: p.subModelId, fieldId: p.fieldId }))}
            />
        </div>
        </div>
    );
};

export default InputParameterConfig;
